import { Panel } from "@/design-system/Panel";
import { poolColour } from "@/design-system/poolColour";
import { StatusBadge } from "@/design-system/StatusBadge";
import { money, percent, signedPercent } from "@/shared/money/money";
import type { RegistryStrategyResponse, StrategyPoolResponse } from "@/shared/api";

import { RiskPolicySection } from "./RiskPolicySection";

/** What each pool is for, in a sentence a client can read. */
/** Only what each pool is for: every rule and number below comes from the
 *  version itself, so a card can never describe a strategy that is not run. */
const PURPOSE: Readonly<Record<string, string>> = {
  PRIME: "Основной пул: самые устойчивые монеты, длинная сетка; цель выхода снижается с возрастом сделки.",
  PULSE: "Быстрый оборот: короткая сетка и быстрый выход.",
  FLASH: "Короткие сделки на резких движениях.",
};

const RETIRED = "Выведен: новых сделок нет, открытые доработают по своим правилам.";

/** «1 уровень», «3 уровня», «15 уровней». */
function levels(count: number): string {
  const tens = count % 100;
  const units = count % 10;
  if (tens >= 11 && tens <= 14) return `${String(count)} уровней`;
  if (units === 1) return `${String(count)} уровень`;
  if (units >= 2 && units <= 4) return `${String(count)} уровня`;
  return `${String(count)} уровней`;
}

type Fact = { readonly label: string; readonly value: string };

/** The same rows for every pool, so the three cards read side by side. */
function poolFacts(pool: StrategyPoolResponse): readonly Fact[] {
  const deepest = pool.grid_drawdowns.at(-1);
  return [
    { label: "Вход", value: pool.entry === "REVERSAL" ? "после подтверждённого разворота" : "по рейтингу" },
    ...(pool.rank_limit == null
      ? []
      : [{
          label: "Отбор",
          value: `топ-${String(pool.rank_limit)} пула${pool.minimum_recovery_rate == null ? "" : `, восстановление от ${percent(pool.minimum_recovery_rate, 0)}`}`,
        }]),
    { label: "Оборот монеты за сутки", value: `от ${money(pool.minimum_turnover, 0)} USDT` },
    { label: "Слотов", value: `до ${String(pool.capacity)}` },
    { label: "Стартовая маржа слота", value: `${money(pool.slot_margin, 0)} USDT` },
    { label: "Плечо", value: `${String(pool.leverage)}×` },
    {
      label: "Сетка",
      value: `${levels(pool.grid_drawdowns.length)}${deepest === undefined ? "" : `, до ${percent(deepest, 1)}`}`,
    },
    { label: "Выход, чистыми", value: pool.exit_net_rate === null ? "—" : `от ${signedPercent(pool.exit_net_rate, 1)}` },
    { label: "Трейлинг от пика", value: pool.trailing_distance === null ? "—" : percent(pool.trailing_distance, 1) },
  ];
}

/** Only a state that needs attention is labelled; the normal one is not. */
function attention(version: RegistryStrategyResponse): string | null {
  if (version.publication_state !== "PUBLISHED") return "Не опубликована";
  if (version.available_for_assignment) return null;
  return version.risk_policy == null ? "Лимиты не заданы" : "Назначение приостановлено";
}

export function StrategyCard({ version, canRevise = false, registryKey = [] }: {
  readonly version: RegistryStrategyResponse;
  readonly canRevise?: boolean;
  readonly registryKey?: readonly unknown[];
}): React.JSX.Element {
  const pool = version.pool ?? null;
  const notice = attention(version);
  return (
    <Panel
      title={version.strategy_id}
      dot={poolColour(version.strategy_id)}
      testId={`registry-${version.strategy_id}`}
      trailing={notice === null ? undefined : (
        <StatusBadge tone="warning" dataset={{ state: version.publication_state }}>{notice}</StatusBadge>
      )}
    >
      <div className="flex flex-col gap-3">
        <p className="text-body-main text-text-secondary">
          {pool !== null && pool.capacity === 0 ? RETIRED : PURPOSE[version.strategy_id] ?? "Стратегия платформы."}
        </p>
        {pool === null ? (
          <p className="text-body-main text-status-warning">Параметры пула недоступны.</p>
        ) : (
          <dl className="divide-y divide-border-subtle" data-testid="strategy-facts">
            {poolFacts(pool).map((fact) => (
              <div key={fact.label} className="flex items-baseline justify-between gap-3 py-2 first:pt-0">
                <dt className="text-body-main text-text-secondary">{fact.label}</dt>
                <dd className="text-right text-body-main text-text-primary tabular-nums">{fact.value}</dd>
              </div>
            ))}
          </dl>
        )}
        <RiskPolicySection version={version} canRevise={canRevise} registryKey={registryKey} />
      </div>
    </Panel>
  );
}
